/**
 * The command palette — ADR 9004, opened with ⌘K or Ctrl+K.
 *
 * It navigates to SCREENS, and to nothing else. The mockup shows articles in the same
 * list; there is no article search endpoint in the contract, and a palette that returned
 * nothing for every article a planner typed would teach them within a day that it does not
 * work. It searches what exists, and gains articles the day the platform publishes a
 * search — at which point the two kinds of result need a visible separation, because
 * "go to a screen" and "open an article" are different actions.
 *
 * **The list is the rail's list.** Both are built from `chapterNavigation()`, so the
 * palette cannot offer a screen the rail does not show, or spell one differently. A screen
 * that is not offered in this release appears here the same way it appears there —
 * readable, `aria-disabled`, and inert — rather than being filtered out. Hiding it would
 * make the palette disagree with the navigation beside it about what the product contains,
 * and "I searched and found nothing" is a worse answer than "it is there, not yet".
 *
 * The match is over the TRANSLATED label, so a planner working in Italian searches in
 * Italian. It is deliberately a plain substring test rather than a fuzzy score: a palette
 * over about thirty-eight entries does not need ranking, and a fuzzy matcher that puts an
 * unexpected row first is worse than one that finds nothing.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as RadixDialog from '@radix-ui/react-dialog';
import { Search } from 'lucide-react';
import { chapterNavigation, screensOf } from '../../features/registry';
import { FOCUS_RING } from '../../primitives/focus';
import { useShellAccess } from '../feedback/access';
import { shellTargets } from './shell-targets';

export interface CommandPaletteProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onNavigate: (to: string) => void;
  /**
   * A path this person is not offered: its route declares a capability the platform denies
   * them (ADR 9037). The rail's own predicate, so the two lists cannot disagree.
   */
  readonly withheld?: (to: string) => boolean;
}

interface Target {
  readonly to: string;
  readonly labelKey: string;
  /** The screen this is a view of, when it is one. */
  readonly parentLabelKey?: string;
  readonly chapterLabelKey: string;
  readonly disabled: boolean;
}

/**
 * Whether the platform is a Mac, for the one string where the two keyboards genuinely
 * differ. Read from `navigator` rather than guessed, and falling back to the Control
 * spelling — which is the right answer for every platform that is not a Mac, and a
 * survivable one on a Mac where ⌘K still works.
 *
 * A session's platform cannot change mid-session, so this is read once at module load
 * rather than on every keydown and every render.
 */
export const isAppleKeyboard: boolean =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);

/** Nothing withheld — a palette mounted without the chrome's predicate offers every entry. */
const NONE_WITHHELD = (): boolean => false;

export function CommandPalette({ open, onOpenChange, onNavigate, withheld = NONE_WITHHELD }: CommandPaletteProps): React.JSX.Element {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const access = useShellAccess();

  // The shell's own screens beside the chapters' — My work, the Profile (ADR 9012), My reports and,
  // for an administrator, User management and activity. The list is `shellTargets`, which the
  // problem report's section picker reads too. The Cockpit is the rail's mark and needs no
  // search; a profile is reached rarely enough that finding it by name is the point.
  const targets = useMemo<readonly Target[]>(() => [
    ...shellTargets(access).map((target): Target => ({ ...target, disabled: false })),
    ...chapterNavigation().flatMap((chapter) =>
    chapter.entries.flatMap((parent) => screensOf([parent]).filter((entry) => !withheld(entry.to)).map((entry) => ({
      to: entry.to,
      labelKey: entry.labelKey,
      // A view of a screen (the markdown's run, output, explanation) is named with its
      // screen: "Output" alone would not say output of what.
      ...(entry === parent ? {} : { parentLabelKey: parent.labelKey }),
      chapterLabelKey: chapter.navLabelKey,
      // Both reasons a screen is not offered collapse to one fact here, exactly as they do
      // in the rail: the chapter is not released, or this one screen is not.
      disabled: chapter.disabled === true || entry.disabled === true,
    })))),
  ], [access, withheld]);

  /** A view is named with its screen — "Price and markdown · Output". */
  const targetLabel = useCallback((target: Target): string => (target.parentLabelKey === undefined
    ? t(target.labelKey)
    : t('nav.subScreen', { screen: t(target.parentLabelKey), view: t(target.labelKey) })), [t]);

  const results = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    if (q === '') return targets;
    return targets.filter((target) =>
      targetLabel(target).toLocaleLowerCase().includes(q)
      || t(target.chapterLabelKey).toLocaleLowerCase().includes(q));
  }, [query, targets, t, targetLabel]);

  /** Reset on every open: a palette that remembers last week's query is a palette that
   *  opens showing the wrong thing. */
  useEffect(() => {
    if (open) { setQuery(''); setCursor(0); }
  }, [open]);

  useEffect(() => { setCursor(0); }, [query]);

  const go = useCallback((target: Target) => {
    if (target.disabled) return;
    onOpenChange(false);
    onNavigate(target.to);
  }, [onNavigate, onOpenChange]);

  /**
   * Arrow keys move the cursor and Enter takes it. The cursor is allowed to land on a
   * disabled row — skipping it would make the keyboard disagree with the pointer about
   * what the list contains — and `go` refuses it, which is the same refusal the rail makes.
   */
  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (results.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((c) => (c + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((c) => (c - 1 + results.length) % results.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const target = results[cursor];
      if (target) go(target);
    }
  };

  useEffect(() => {
    listRef.current?.querySelector('[data-cursor="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        {/* The same scrim `Modal` uses — the page plane at 70%. No new token: a second
            scrim would be a second answer to "how dark is an overlay". */}
        <RadixDialog.Overlay className="bg-scrim z-modal fixed inset-0" />
        {/*
          `top-16` — 64px, and it has to be a key the TOKEN spacing scale actually has.

          This read `top-24`. The Tailwind theme REPLACES the default spacing scale with
          the design tokens (0/4/8/12/16/24/32/48/64px, keyed 0/1/2/3/4/6/8/12/16), so
          `24` is not a key and the class emitted no rule at all. `top` stayed `auto`,
          which for a fixed box means its STATIC position — the end of `<body>`, below the
          whole document. The palette opened at y=3563 on a 900px viewport on
          `/inseason/markdown`: ⌘K dimmed the page and showed nothing.

          `start-0 end-0 mx-auto`, not `start-1/2 -translate-x-1/2` (designer audit B8): a
          translate is physical, so in a right-to-left document the palette sat half its width
          off centre. Pinned to both inline edges and centred by its auto margins, it is
          centred in either direction.
        */}
        <RadixDialog.Content
          onKeyDown={onKeyDown}
          className="bg-surface-raised border-hairline shadow-overlay rounded-control z-modal fixed start-0 end-0 top-16 mx-auto flex max-h-[60vh] w-[min(560px,92vw)] flex-col overflow-hidden border"
        >
          <RadixDialog.Title className="sr-only">{t('palette.title')}</RadixDialog.Title>
          <RadixDialog.Description className="sr-only">{t('palette.description')}</RadixDialog.Description>

          <div className="border-hairline flex items-center gap-2 border-b px-3 py-2">
            <Search size={16} aria-hidden className="text-ink-secondary shrink-0" />
            <input
              // A palette that opens without focus in its field is one the planner has to
              // click before typing, which defeats the shortcut that opened it. Radix
              // returns focus to the opener on close, so nothing is stranded.
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label={t('palette.placeholder')}
              placeholder={t('palette.placeholder')}
              className={`text-body text-ink-primary placeholder:text-ink-muted w-full bg-transparent ${FOCUS_RING}`}
            />
          </div>

          {results.length === 0 ? (
            <p className="text-body text-ink-secondary px-3 py-6 text-center">{t('palette.noResults')}</p>
          ) : (
            <ul ref={listRef} className="overflow-y-auto py-1">
              {results.map((target, index) => (
                <li key={target.to}>
                  <button
                    type="button"
                    data-cursor={index === cursor ? 'true' : undefined}
                    onClick={() => go(target)}
                    onMouseMove={() => setCursor(index)}
                    aria-disabled={target.disabled || undefined}
                    title={target.disabled ? t('nav.screenUnavailable') : undefined}
                    className={[
                      'flex w-full items-baseline gap-2 px-3 py-2 text-start',
                      index === cursor ? 'bg-surface-sunken' : '',
                      target.disabled ? 'cursor-not-allowed' : '',
                      FOCUS_RING,
                    ].join(' ')}
                  >
                    <span className={target.disabled ? 'text-body text-ink-secondary' : 'text-body text-ink-primary'}>
                      {targetLabel(target)}
                    </span>
                    <span className="text-caption text-ink-secondary truncate">
                      {t(target.chapterLabelKey)}
                    </span>
                    {/* §12.6 — the unavailable state never rests on colour. It is a word,
                        beside `aria-disabled` and the title. */}
                    {target.disabled && (
                      <span className="text-micro text-ink-secondary ms-auto shrink-0">
                        {t('nav.screenUnavailable')}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
